import { IconButton } from '@atlaskit/button/new';
import GenerativeIndicatorIcon from '@atlaskit/icon-lab/core/generative-indicator';
import { Box } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import DropdownMenu, { type OnOpenChangeArgs } from '@databases/atlaskit/DropdownMenu';
import { contextualMenuState } from '@databases/contextual-menu/contextual-menu-state';
import { messages } from '@databases/intl/messages';
import composeRefs from '@seznam/compose-react-refs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { subscribe } from 'valtio';

import { useFormatRelativeTime } from '@canvas/intl/format-relative-time';

import { AiSummaryRunDropdownItems } from './ai-summary-run-dropdown-items.tsx';
import { useIsAiSummaryFieldEnabled } from './use-is-ai-summary-field-enabled.ts';

export interface AiSummaryFieldHeaderActionProps {
  confirmationKey: string;
  hasUserEditedCells: () => boolean;
  lastRunAt?: string;
  onRerunEntireColumn: () => void;
  onRerunEmptyEntries: () => void;
  isDisabled?: boolean;
}

export const AiSummaryFieldHeaderAction = ({
  confirmationKey,
  hasUserEditedCells,
  lastRunAt,
  onRerunEntireColumn,
  onRerunEmptyEntries,
  isDisabled = false
}: AiSummaryFieldHeaderActionProps) => {
  const { formatMessage } = useIntl();
  const relativeLastRunTime = useFormatRelativeTime(lastRunAt);
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const isAiFieldsSummaryEnabled = useIsAiSummaryFieldEnabled();
  const tooltipContent = relativeLastRunTime
    ? formatMessage(messages['field-actions.ai-summary-rerun-tooltip'], {
        relativeTime: relativeLastRunTime
      })
    : formatMessage(messages['field-actions.ai-summary-run-tooltip']);

  const handleOpenChange = useCallback(({ isOpen }: OnOpenChangeArgs) => {
    if (isOpen) {
      contextualMenuState.closeMenu();
    }
    setIsOpen(isOpen);
  }, []);

  useEffect(
    () =>
      subscribe(contextualMenuState, () => {
        if (isOpenRef.current && contextualMenuState.isAnyMenuOpen()) {
          setIsOpen(false);
        }
      }),
    []
  );

  if (!isAiFieldsSummaryEnabled) {
    return null;
  }

  return (
    <Box testId="ai-summary-field-header-action">
      <DropdownMenu
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        spacing="compact"
        placement="bottom-end"
        testId="ai-summary-field-header-action-menu"
        trigger={({ triggerRef, ...triggerProps }) => (
          <Tooltip
            content={tooltipContent}
            position="top"
            hideTooltipOnClick
            // Prevent Tooltip's hidden announcement from affecting the inline header layout.
            isScreenReaderAnnouncementDisabled
          >
            {(tooltipProps) => {
              const { onClick, ...restTriggerProps } = triggerProps;

              return (
                <IconButton
                  {...tooltipProps}
                  {...restTriggerProps}
                  appearance="subtle"
                  icon={(iconProps) => (
                    <GenerativeIndicatorIcon
                      {...iconProps}
                      size="small"
                      spacing="compact"
                      color="currentColor"
                    />
                  )}
                  isSelected={isOpen}
                  isDisabled={isDisabled}
                  label={formatMessage(
                    messages['field-actions.ai-summary-refresh.non-final']
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    tooltipProps.onClick(event);
                    onClick?.(event);
                  }}
                  ref={composeRefs(triggerRef, tooltipProps.ref)}
                  spacing="compact"
                  testId="ai-summary-field-header-action-trigger"
                />
              );
            }}
          </Tooltip>
        )}
      >
        <AiSummaryRunDropdownItems
          confirmationKey={confirmationKey}
          hasUserEditedCells={hasUserEditedCells}
          onRerunEntireColumn={onRerunEntireColumn}
          onRerunEmptyEntries={onRerunEmptyEntries}
          isDisabled={isDisabled}
        />
      </DropdownMenu>
    </Box>
  );
};
